'use client';

import { useCallback, useEffect, useMemo, useRef, startTransition, useState } from 'react';
import { getRatelimitMetrics, type RatelimitMetricsEntry } from '@/lib/urm-api';
import type { RateLimitMetricsSnapshot } from '@/lib/metrics-snapshot';
import {
  LIVE_TAIL_INTERVAL_MS,
  PAGE_LIMIT,
  type MetricsSample,
} from '../types';

interface UseMetricsArgs {
  fromMs: number | null;     // null = "no fetch yet"
  toMs: number | null;       // null = "now (live)"
  liveTail: boolean;
  snapshot?: RateLimitMetricsSnapshot | null;
}

interface UseMetricsResult {
  samples: MetricsSample[];
  loading: boolean;
  error: string | null;
  // Trigger a fresh fetch with new bounds (e.g. after picking a new range).
  refetch: () => void;
  // Wall-clock ms when the next live-tail poll will fire, or null if not tailing.
  nextPollAtMs: number | null;
  // Wall-clock ms when samples were last updated, or null before first fetch.
  lastFetchMs: number | null;
  loadedFromMs: number | null;
  loadedToMs: number | null;
}

function entryToSample(e: RatelimitMetricsEntry): MetricsSample {
  return {
    ts_ms:                e.ts_ms,
    pod:                  e.pod,
    limit_calculated:     e.limit_calculated,
    limit_to_publish:     e.limit_to_publish,
    limit_with_grace:     e.limit_with_grace,
    inflight:             e.inflight,
    server_latency:       e.server_latency,
    server_cpu:           e.server_cpu,
    server_memory:        e.server_memory,
    server_total_requests: e.server_total_requests,
    redis_cpu:            e.redis_cpu,
    redis_memory:         e.redis_memory,
    opensearch_cpu:       e.opensearch_cpu,
    opensearch_memory:    e.opensearch_memory,
    opensearch_queues:    e.opensearch_queues,
  };
}

export function useMetrics({
  fromMs,
  toMs,
  liveTail,
  snapshot = null,
}: UseMetricsArgs): UseMetricsResult {
  const [samples, setSamples] = useState<MetricsSample[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextPollAtMs, setNextPollAtMs] = useState<number | null>(null);
  const [lastFetchMs, setLastFetchMs] = useState<number | null>(null);
  const [loadedFromMs, setLoadedFromMs] = useState<number | null>(null);
  const [loadedToMs, setLoadedToMs] = useState<number | null>(null);

  // Cursor for the next fetch in tailing mode. Both runOnce and tailOnce
  // write to it; tailOnce reads it.
  const cursorRef = useRef<string | null>(null);

  // Per-operation run-id tokens. Each effect bumps ONLY its own ref on
  // cleanup, so the two operations don't accidentally invalidate each other.
  const fetchRunIdRef = useRef(0);
  const tailRunIdRef = useRef(0);

  // Active poll timer handle (cleared on cleanup or when liveTail flips off).
  const pollTimerRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const fetchPage = useCallback(async (
    args: { from?: number; to?: number; startAfter?: string }
  ): Promise<{ entries: RatelimitMetricsEntry[]; nextCursor: string; truncated: boolean }> => {
    const resp = await getRatelimitMetrics({ ...args, limit: PAGE_LIMIT });
    return { entries: resp.entries, nextCursor: resp.next_cursor, truncated: resp.truncated };
  }, []);

  const runOnce = useCallback(async () => {
    if (fromMs === null || snapshot !== null) return;
    fetchRunIdRef.current += 1;
    const myRunId = fetchRunIdRef.current;
    const isCurrent = () => fetchRunIdRef.current === myRunId;

    setLoading(true);
    setError(null);
    const collected: MetricsSample[] = [];
    let cursor = '';
    try {
      let resp = await fetchPage({ from: fromMs, to: toMs ?? undefined });
      for (const e of resp.entries) collected.push(entryToSample(e));
      cursor = resp.nextCursor;
      while (resp.truncated && isCurrent() && cursor) {
        resp = await fetchPage({ startAfter: cursor, to: toMs ?? undefined });
        for (const e of resp.entries) collected.push(entryToSample(e));
        cursor = resp.nextCursor;
      }
      if (!isCurrent()) return;
      setSamples(collected);
      const fetchedAtMs = Date.now();
      setLastFetchMs(fetchedAtMs);
      setLoadedFromMs(fromMs);
      setLoadedToMs(toMs ?? fetchedAtMs);
      cursorRef.current = cursor;
    } catch (e: unknown) {
      if (!isCurrent()) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [fromMs, toMs, fetchPage, snapshot]);

  const tailOnce = useCallback(async (myRunId: number) => {
    if (!cursorRef.current) return;
    const isCurrent = () => tailRunIdRef.current === myRunId;
    try {
      const resp = await fetchPage({ startAfter: cursorRef.current });
      if (!isCurrent()) return;
      if (resp.entries.length > 0) {
        const newSamples = resp.entries.map(entryToSample);
        setSamples((prev) => prev.concat(newSamples));
        setLastFetchMs(Date.now());
      }
      cursorRef.current = resp.nextCursor;
    } catch (e: unknown) {
      if (!isCurrent()) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [fetchPage]);

  // Effect: refetch when bounds change. Cleanup bumps the FETCH run id only,
  // so any in-flight runOnce is invalidated; tailOnce is not touched.
  useEffect(() => {
    startTransition(() => { void runOnce(); });
    return () => {
      fetchRunIdRef.current += 1;
    };
  }, [runOnce]);

  // Effect: drive the tail loop. Cleanup bumps the TAIL run id only and
  // clears the poll timer, leaving any in-flight runOnce untouched.
  useEffect(() => {
    if (snapshot !== null || !liveTail || loading) {
      stopPolling();
      startTransition(() => { setNextPollAtMs(null); });
      return;
    }
    const myRunId = tailRunIdRef.current;
    const tick = async () => {
      await tailOnce(myRunId);
      if (tailRunIdRef.current !== myRunId) return;
      startTransition(() => { setNextPollAtMs(Date.now() + LIVE_TAIL_INTERVAL_MS); });
      pollTimerRef.current = window.setTimeout(tick, LIVE_TAIL_INTERVAL_MS);
    };
    startTransition(() => { setNextPollAtMs(Date.now() + LIVE_TAIL_INTERVAL_MS); });
    pollTimerRef.current = window.setTimeout(tick, LIVE_TAIL_INTERVAL_MS);
    return () => {
      tailRunIdRef.current += 1;
      stopPolling();
      startTransition(() => { setNextPollAtMs(null); });
    };
  }, [liveTail, loading, snapshot, tailOnce, stopPolling]);

  const refetch = useCallback(() => { void runOnce(); }, [runOnce]);
  const snapshotResult = useMemo(() => {
    if (snapshot === null) return null;
    const snapshotFromMs = fromMs ?? snapshot.loadedFromMs;
    const snapshotToMs = toMs ?? snapshot.loadedToMs;
    return {
      samples: snapshot.samples.filter((sample) => (
        sample.ts_ms >= snapshotFromMs && sample.ts_ms <= snapshotToMs
      )),
      loadedFromMs: snapshotFromMs,
      loadedToMs: snapshotToMs,
    };
  }, [fromMs, snapshot, toMs]);

  if (snapshot !== null && snapshotResult !== null) {
    return {
      samples: snapshotResult.samples,
      loading: false,
      error: null,
      refetch,
      nextPollAtMs: null,
      lastFetchMs: snapshot.lastUpdatedMs,
      loadedFromMs: snapshotResult.loadedFromMs,
      loadedToMs: snapshotResult.loadedToMs,
    };
  }

  return {
    samples,
    loading,
    error,
    refetch,
    nextPollAtMs,
    lastFetchMs,
    loadedFromMs,
    loadedToMs,
  };
}
